import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { messagingClient } from '../../../api/client';
import { MessagingSettings, MessagingSettingsUpdateRequest, MessagingStatus, MessagingTestRequest } from '../../../api/schema';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import { ListMessagingLogsQuery, MessagingLogsListType, MessagingLogsType, MessagingState } from './messagingInterface';

export const logsAdapter = createEntityAdapter<MessagingLogsListType>({
  selectId: (logs) => logs.id || '',
});

export const listMessagingLogs = createAsyncThunk('messaging/listMessagingLogs', async (query: ListMessagingLogsQuery) => {
  const argumentsKeys = ['channel', 'kind', 'status', 'recipient', 'createdBegin', 'createdEnd', 'offset', 'limit', 'options'];

  return messagingClient.messagingLogs.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      channel?: string,
      kind?: string,
      status?: string,
      recipient?: string,
      createdBegin?: string,
      createdEnd?: string,
      offset?: number,
      limit?: number,
      options?: Record<string, unknown>,
    ],
  );
});

export const getMessagingStatus = createAsyncThunk('messaging/getMessagingStatus', async () => messagingClient.messagingStatus());
export const getMessagingSettings = createAsyncThunk('messaging/getMessagingSettings', async () => messagingClient.messagingSettings());
export const updateMessagingSettings = createAsyncThunk('messaging/updateMessagingSettings', async (request: MessagingSettingsUpdateRequest) =>
  messagingClient.messagingSettingsUpdate(request),
);
export const sendMessagingTest = createAsyncThunk('messaging/sendMessagingTest', async (request: MessagingTestRequest) =>
  messagingClient.messagingTest(request),
);

export const messagingSlice = createSlice({
  name: 'messaging',
  initialState: {
    logs: logsAdapter.getInitialState(),
  } as MessagingState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listMessagingLogs.pending, (state: MessagingState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listMessagingLogs: true };
    });
    builder.addCase(listMessagingLogs.fulfilled, (state: MessagingState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listMessagingLogs: false };
      if (action.payload.data?.result) {
        logsAdapter.upsertOne(state.logs, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listMessagingLogs.rejected, (state: MessagingState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listMessagingLogs: false };
    });
    builder.addCase(getMessagingStatus.pending, (state: MessagingState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getMessagingStatus: true };
    });
    builder.addCase(getMessagingStatus.fulfilled, (state: MessagingState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getMessagingStatus: false };
      state.status = action.payload.data?.result;
    });
    builder.addCase(getMessagingStatus.rejected, (state: MessagingState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getMessagingStatus: false };
    });
    builder.addCase(getMessagingSettings.pending, (state: MessagingState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getMessagingSettings: true };
    });
    builder.addCase(getMessagingSettings.fulfilled, (state: MessagingState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getMessagingSettings: false };
      state.settings = action.payload.data?.result;
    });
    builder.addCase(getMessagingSettings.rejected, (state: MessagingState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getMessagingSettings: false };
    });
    builder.addCase(updateMessagingSettings.pending, (state: MessagingState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), updateMessagingSettings: true };
    });
    builder.addCase(updateMessagingSettings.fulfilled, (state: MessagingState, action: AnyAction) => {
      state.fetching = { ...state.fetching, updateMessagingSettings: false };
      state.settings = action.payload.data?.result;
    });
    builder.addCase(updateMessagingSettings.rejected, (state: MessagingState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, updateMessagingSettings: false };
    });
    builder.addCase(sendMessagingTest.pending, (state: MessagingState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), sendMessagingTest: true };
    });
    builder.addCase(sendMessagingTest.fulfilled, (state: MessagingState) => {
      state.fetching = { ...state.fetching, sendMessagingTest: false };
    });
    builder.addCase(sendMessagingTest.rejected, (state: MessagingState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, sendMessagingTest: false };
    });
  },
});

export const { selectById: selectMessagingLogsById } = logsAdapter.getSelectors((state: RootState) => state.messaging.logs);

export const selectMessagingLogsSelector = createSelector([selectMessagingLogsById], (logs) => ({
  pagination: getPagination(logs),
  result: logs?.result,
}));

export const getMessagingLogs =
  (keys: ListMessagingLogsQuery) =>
  (state: RootState): MessagingLogsType =>
    selectMessagingLogsSelector(state, keysToId(keys));

export const getMessagingStatusInfo = (state: RootState): MessagingStatus | undefined => state.messaging.status;
export const getMessagingSettingsInfo = (state: RootState): MessagingSettings | undefined => state.messaging.settings;
export const messagingSettingsFetching = (state: RootState): boolean => state.messaging.fetching?.getMessagingSettings !== false;
export const messagingSettingsUpdating = (state: RootState): boolean | undefined => state.messaging.fetching?.updateMessagingSettings;
export const messagingLogsFetching = (state: RootState): boolean => state.messaging.fetching?.listMessagingLogs !== false;
export const messagingTestSending = (state: RootState): boolean | undefined => state.messaging.fetching?.sendMessagingTest;
export default messagingSlice.reducer;
